import { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import ArrangementCardList from '../ArrangementCard/ArrangementCardList';
import { ImageFrame } from '../ArrangementCard/ArrangementCard';
import { LinkHighlightButton, SecondaryButton } from '../HighlightButton/HighlightButton';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import SidebarMenu from '../SidebarMenu/SidebarMenu';

import { useProducts } from '../../hooks/useProducts';
import styles from '../../css/Arrangements.module.css';
import appStyles from '../../css/App.module.css';

const handleMenuItemClick = async (item) => {
  const { logAnalyticsEvent } = await import('../../utils/analyticsUtils');
  logAnalyticsEvent('menu_click', {
    label: item.label,
    path: item.path + (item.hash || '')
  });
};

const handleCommissionClick = async (type) => {
  const { logAnalyticsEvent } = await import('../../utils/analyticsUtils');
  logAnalyticsEvent(`commission_${type}_button_click`);
};

function Arrangements() {
  const { products, loading, error } = useProducts();
  const [activeSection, setActiveSection] = useState('acappella');
  const acappellaRef = useRef(null);
  const pianoRef = useRef(null);
  const transcriptionsRef = useRef(null);

  // Menu items for the sidebar
  const menuItems = useMemo(() => [
    { label: 'A Cappella Arrangements', path: '/work', hash: '#acappella' },
    { label: 'Solo Piano Arrangements', path: '/work', hash: '#piano' },
    { label: 'Transcriptions', path: '/work', hash: '#transcriptions' }
  ], []);

  // derived state computed by filtering existing products
  const soloPianoProducts = useMemo(
    () => products.filter(product => product.type === "arrangement" && product.ensemble === "Solo Piano" && product.visibility === "Public"),
    [products]
  );

  const acappellaProducts = useMemo(
    () => products.filter(product => product.type === "arrangement" && product.ensemble === "Choir/A Cappella" && product.visibility === "Public"),
    [products]
  )

  const transcriptionsProducts = useMemo(
    () => products.filter(product => product.type === "transcription" && product.visibility === "Public"),
    [products]
  )

  const handleIntersect = (elements) => {
    elements.forEach((el) => {
      if (el.isIntersecting) {
        // el.current is the IntersectionObserverEntry
        // el.target is the DOM element.
        setActiveSection(el.target?.id)
      }
    });
  }

  const pauseAllAudio = (excludeRef) => {
    document.querySelectorAll('audio').forEach(audio => {
      if (!excludeRef || audio !== excludeRef) {
        audio.pause();
      }
    });
  }

  // Pause all YouTube iframes
  const pauseAllVideo = (excludeRef) => {
    document.querySelectorAll('iframe').forEach(iframe => {
      if (iframe.src.includes('youtube.com') && (!excludeRef || iframe !== excludeRef)) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*'
        );
      }
    });
  }

  // Exclude the current card's media from being paused
  const pauseAllMedia = useCallback((audioRef, iframeRef) => {
    // Must call both methods below, as clicking on audio should also pause iframes and vice versa
    pauseAllAudio(audioRef);
    pauseAllVideo(iframeRef);
  }, []);

  useEffect(() => {
    // refs are not loaded yet
    if (loading) return;

    const intersectionObserver = new IntersectionObserver(handleIntersect);

    const sectionElements = [acappellaRef.current, pianoRef.current, transcriptionsRef.current].filter(Boolean);
    sectionElements.forEach((sectionElement) => intersectionObserver.observe(sectionElement));

    return () => {
      // .disconnect() rather than call .unobserve() on each individual element
      intersectionObserver.disconnect();
    };
  }, [loading]);

  // Pause all media when navigating away from this page
  useEffect(() => {
    return () => {
      pauseAllMedia();
    };
  }, [pauseAllMedia]);

  // Show loading state
  if (loading) {
    return (
      <main className="mainContainer centeredHorizontallyOnMobile">
        <div className={styles.contentContainer}>
          <LoadingSpinner message="Loading..." />
        </div>
      </main>
    );
  }

  // Show error state
  if (error) {
    return (
      <main className="mainContainer centeredHorizontallyOnMobile">
        <div className={styles.contentContainer}>
          <p>There was an error loading.</p>
          <div style={{ marginTop: 10 }}>
            <SecondaryButton onClick={() => window.location.reload()}>Retry</SecondaryButton>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="centeredHorizontallyOnMobile">
      <div className={styles.contentContainer}>
        <aside className={styles.sidebarContainer}>
          <SidebarMenu
            items={menuItems}
            currentPath={window.location.pathname}
            activeSection={activeSection}
            onItemClick={handleMenuItemClick}
          />
        </aside>

        <article className={appStyles.appContainer}>
          <section id="acappella" ref={acappellaRef}>
            <h1 className={styles.pageTitle}><a href="#acappella" className={styles.titleLink}>A CAPPELLA ARRANGEMENTS</a></h1>

            <div className={styles.cardContainer}>
              <ArrangementCardList products={acappellaProducts} pauseAllMedia={pauseAllMedia} listName="A Cappella/Choir" />

              <ImageFrame>
                <div className={styles.commissionCard}>
                  <h4 className={styles.ctaText} style={{ paddingBottom: 10 }}>Didn&apos;t find anything up your alley?</h4>
                  <LinkHighlightButton
                    to="/commissions"
                    onClick={() => handleCommissionClick('acappella')}
                  >
                    COMMISSION ME
                  </LinkHighlightButton>
                </div>
              </ImageFrame>
            </div>
          </section>

          <section id="piano" ref={pianoRef}>
            <h1 className={styles.pageTitle}><a href="#piano" className={styles.titleLink}>SOLO PIANO ARRANGEMENTS</a></h1>

            <div className={styles.cardContainer}>
              <ArrangementCardList products={soloPianoProducts} pauseAllMedia={pauseAllMedia} listName="Solo Piano" />

              <ImageFrame>
                <div className={styles.commissionCard}>
                  <h4 className={styles.ctaText} style={{ paddingBottom: 10 }}>Didn&apos;t find anything up your alley?</h4>
                  <LinkHighlightButton
                    to="/commissions"
                    onClick={() => handleCommissionClick('piano')}
                  >
                    COMMISSION ME
                  </LinkHighlightButton>
                </div>
              </ImageFrame>
            </div>
          </section>

          <section id="transcriptions" ref={transcriptionsRef}>
            <h1 className={styles.pageTitle}><a href="#transcriptions" className={styles.titleLink}>TRANSCRIPTIONS</a></h1>

            <div className={styles.cardContainer}>
              <ArrangementCardList products={transcriptionsProducts} pauseAllMedia={pauseAllMedia} listName="Transcription" />

              <ImageFrame>
                <div className={styles.commissionCard}>
                  <h4 className={styles.ctaText} style={{ paddingBottom: 10 }}>Didn&apos;t find anything up your alley?</h4>

                  <LinkHighlightButton
                    to="/commissions"
                    onClick={() => handleCommissionClick('transcription')}
                  >
                    COMMISSION ME
                  </LinkHighlightButton>
                </div>
              </ImageFrame>
            </div>
          </section>
        </article>
      </div>
    </main>
  )
}

export default Arrangements;
